/**
 * PhoneOperatorsPage — Jun 2026
 * Admin list of mobile operators + mandatory 3-digit prefixes (e.g. اتصالات → 050).
 * Route: /control/cloudgm/customer-dropdowns/phone-operators
 * Platforms read it server-to-server: GET /api/platform-lists/phone-operators
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit3, Trash2, Loader2, Phone, Hash, Sparkles, X, Power } from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AssetThumb } from '../components/shared-assets/AssetThumb';
import { OperatorLogoPicker } from '../components/phone-operators/OperatorLogoPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CODE_RE = /^\d{3}$/;

export default function PhoneOperatorsPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | operator

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/phone-operators`, { headers: getAuthHeaders() });
      setOps(r.data?.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (op) => {
    try {
      await axios.put(`${API}/admin/phone-operators/${op.id}`, { ...op, is_active: !op.is_active }, { headers: getAuthHeaders() });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const remove = async (op) => {
    if (!window.confirm(t(`حذف المزوّد "${op.name_ar}" وكل أكواده؟`, `Delete "${op.name_en}" and its codes?`))) return;
    try {
      await axios.delete(`${API}/admin/phone-operators/${op.id}`, { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted')); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
      <DashboardHeader showBackButton backPath="/control/cloudgm/customer-dropdowns" />
      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
            { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
            { label_ar: 'القوائم المنسدلة للتحكم بمنصة العملاء', label_en: 'Customer Platform Dropdown Lists', path: '/control/cloudgm/customer-dropdowns' },
            { label_ar: 'قوائم مشغّلي أرقام الهواتف', label_en: 'Phone Operators Lists', path: null },
          ]} />
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="phone-operators-page">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">{t('قوائم مشغّلي أرقام الهواتف', 'Phone Operators Lists')}</h1>
            <p className="text-sm text-white/50 mt-1 max-w-2xl">
              {t('اسم المزوّد بالعربي والإنجليزي، وشعاره من قاعدة الصور، وتحته أكواده (3 أرقام). منصّتا العملاء والتجار تسحبان القائمة مع كود الشعار من هنا بمفتاح الخادم.',
                 'Operator name (AR/EN), logo from the shared library, and its 3-digit prefixes. Customer & merchant platforms pull the list (with logo code) server-to-server with their key.')}
            </p>
          </div>
          <Button onClick={() => setEditing({})} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="operator-add-btn">
            <Plus className="w-4 h-4 me-1" />{t('إضافة مزوّد', 'Add operator')}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-white/40"><Loader2 className="w-7 h-7 animate-spin" /></div>
        ) : ops.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-white/10 text-white/40" data-testid="operators-empty">
            <Phone className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {t('لا يوجد مزوّدون بعد — أضف أول مزوّد مثل «اتصالات» وكوده 050', 'No operators yet — add the first one, e.g. "Etisalat" with 050')}
          </div>
        ) : (
          <div className="space-y-3">
            {ops.map((op) => (
              <div key={op.id} className={`rounded-2xl border p-4 flex flex-wrap items-center gap-4 ${op.is_active ? 'border-white/10 bg-white/[0.02]' : 'border-white/5 bg-white/[0.01] opacity-60'}`}
                   data-testid={`operator-row-${op.id}`}>
                {op.logo_asset_id ? (
                  <AssetThumb assetId={op.logo_asset_id} version={op.updated_at} className="w-11 h-11" alt={op.name_ar} />
                ) : (
                  <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-emerald-400" />
                  </div>
                )}
                <div className="min-w-[160px]">
                  <div className="text-white font-bold">{op.name_ar}</div>
                  <div className="text-white/50 text-xs" dir="ltr">{op.name_en}</div>
                </div>
                <div className="flex flex-wrap gap-1.5 flex-1" dir="ltr" data-testid={`operator-codes-${op.id}`}>
                  {(op.codes || []).map((c) => (
                    <span key={c} className="px-2.5 py-1 rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/40 text-[#d4af37] font-mono text-sm">{c}</span>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => toggleActive(op)} title={op.is_active ? t('إيقاف', 'Deactivate') : t('تفعيل', 'Activate')}
                          className={`h-9 px-3 rounded-lg border text-xs flex items-center gap-1 ${op.is_active ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-white/10 text-white/50'}`}
                          data-testid={`operator-toggle-${op.id}`}>
                    <Power className="w-3.5 h-3.5" />{op.is_active ? t('مفعّل', 'Active') : t('متوقف', 'Off')}
                  </button>
                  <button onClick={() => setEditing(op)} className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 flex items-center justify-center" data-testid={`operator-edit-${op.id}`}>
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(op)} className="h-9 w-9 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 flex items-center justify-center" data-testid={`operator-delete-${op.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {editing !== null && (
        <OperatorDialog op={editing} others={ops.filter((o) => o.id !== editing?.id)} t={t}
                        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function OperatorDialog({ op, others = [], t, onClose, onSaved }) {
  const isEdit = Boolean(op?.id);
  const [nameAr, setNameAr] = useState(op?.name_ar || '');
  const [nameEn, setNameEn] = useState(op?.name_en || '');
  const [codes, setCodes] = useState(op?.codes || []);
  const [logo, setLogo] = useState(op?.logo_asset_id || null);
  const [codeInput, setCodeInput] = useState('');
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);

  const codeError = codeInput && !CODE_RE.test(codeInput) ? t('الكود 3 أرقام فقط', 'Code must be exactly 3 digits') : '';
  const sharedWith = (c) => others.filter((o) => (o.codes || []).includes(c)).map((o) => (t(o.name_ar, o.name_en)));
  const sharedNotes = codes.map((c) => ({ c, names: sharedWith(c) })).filter((x) => x.names.length);
  const canSave = nameAr.trim() && nameEn.trim() && codes.length > 0 && !saving;

  const addCode = () => {
    const c = codeInput.trim();
    if (!CODE_RE.test(c)) { toast.error(t('الكود يجب أن يكون 3 أرقام فقط، مثل 050', 'Code must be exactly 3 digits, e.g. 050')); return; }
    if (codes.includes(c)) { toast.error(t('الكود مضاف مسبقاً', 'Code already added')); return; }
    setCodes([...codes, c]); setCodeInput('');
  };
  const translate = async () => {
    if (!nameAr.trim()) { toast.error(t('اكتب الاسم بالعربي أولاً', 'Enter the Arabic name first')); return; }
    setTranslating(true);
    try {
      const r = await axios.post(`${API}/translate`, { text: nameAr.trim(), source: 'ar', target: 'en' }, { headers: getAuthHeaders() });
      if (r.data?.translated) setNameEn(r.data.translated);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setTranslating(false); }
  };
  const save = async () => {
    if (!codes.length) { toast.error(t('أضف كوداً واحداً على الأقل', 'Add at least one code')); return; }
    setSaving(true);
    try {
      const body = { name_ar: nameAr.trim(), name_en: nameEn.trim(), codes, logo_asset_id: logo || null, is_active: op?.is_active ?? true, sort_order: op?.sort_order ?? 0 };
      if (isEdit) await axios.put(`${API}/admin/phone-operators/${op.id}`, body, { headers: getAuthHeaders() });
      else await axios.post(`${API}/admin/phone-operators`, body, { headers: getAuthHeaders() });
      toast.success(t('تم الحفظ', 'Saved')); onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(' • ') : (d || e.message));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0b0b14] border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="operator-dialog">
        <DialogHeader><DialogTitle>{isEdit ? t('تعديل مزوّد', 'Edit operator') : t('إضافة مزوّد', 'Add operator')}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-white/60">{t('اسم المزوّد (عربي) *', 'Operator name (Arabic) *')}</label>
            <Input value={nameAr} onChange={(e) => setNameAr(e.target.value)} placeholder={t('مثال: اتصالات', 'e.g. اتصالات')} className="bg-white/5 border-white/10 mt-1" data-testid="operator-name-ar" />
          </div>
          <div>
            <label className="text-xs text-white/60">{t('اسم المزوّد (إنجليزي) *', 'Operator name (English) *')}</label>
            <div className="flex gap-2 mt-1">
              <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="e.g. Etisalat" dir="ltr" className="bg-white/5 border-white/10" data-testid="operator-name-en" />
              <Button type="button" variant="outline" onClick={translate} disabled={translating} className="border-[#d4af37]/40 text-[#d4af37] shrink-0" data-testid="operator-translate-btn">
                {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <div>
            <label className="text-xs text-white/60 flex items-center gap-1"><Hash className="w-3 h-3" />{t('الأكواد (3 أرقام) — كود واحد على الأقل *', 'Codes (3 digits) — at least one *')}</label>
            <div className="flex gap-2 mt-1" dir="ltr">
              <Input value={codeInput} inputMode="numeric" maxLength={3}
                     onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 3))}
                     onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCode(); } }}
                     placeholder="050" className="bg-white/5 border-white/10 font-mono w-32" data-testid="operator-code-input" />
              <Button type="button" onClick={addCode} disabled={!CODE_RE.test(codeInput)} className="bg-white/10 hover:bg-white/20 text-white" data-testid="operator-code-add-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {codeError && <p className="text-[11px] text-red-300 mt-1" data-testid="operator-code-error">{codeError}</p>}
            <div className="flex flex-wrap gap-1.5 mt-2 min-h-[28px]" dir="ltr" data-testid="operator-codes-list">
              {codes.map((c) => (
                <span key={c} className={`pl-2.5 pr-1 py-1 rounded-lg font-mono text-sm flex items-center gap-1 border ${sharedWith(c).length ? 'bg-amber-500/10 border-amber-500/40 text-amber-300' : 'bg-[#d4af37]/10 border-[#d4af37]/40 text-[#d4af37]'}`}
                      title={sharedWith(c).length ? t(`مشترك مع: ${sharedWith(c).join('، ')}`, `Shared with: ${sharedWith(c).join(', ')}`) : ''}>
                  {c}<button type="button" onClick={() => setCodes(codes.filter((x) => x !== c))} className="hover:text-white" data-testid={`operator-code-remove-${c}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
              {codes.length === 0 && <span className="text-[11px] text-amber-300/80">{t('لم يُضف أي كود بعد — الحفظ معطّل', 'No code yet — save is disabled')}</span>}
            </div>
            {sharedNotes.length > 0 && (
              <p className="text-[11px] text-amber-300/90 mt-2" data-testid="operator-shared-note">
                {t('ملاحظة (غير مانعة): ', 'Note (not blocking): ')}
                {sharedNotes.map((x) => t(`الكود ${x.c} مستخدم أيضاً مع ${x.names.join('، ')}`, `code ${x.c} is also used by ${x.names.join(', ')}`)).join(' · ')}
                {t(' — يمكن أن يعمل الكود مع أكثر من مشغّل.', ' — a code may belong to more than one operator.')}
              </p>
            )}
          </div>
          <OperatorLogoPicker value={logo} onChange={setLogo} t={t} />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white/70">{t('إلغاء', 'Cancel')}</Button>
          <Button onClick={save} disabled={!canSave} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="operator-save-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin me-1" />}{isEdit ? t('حفظ التغييرات', 'Save changes') : t('إضافة', 'Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
